import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Compact a number for stat displays. Numbers >= 10,000 become 10K, 1.2M, 3.4B etc.
 * Smaller numbers are returned as-is using locale formatting.
 */
export function formatCompactNumber(value: number | null | undefined): string {
  const n = Number(value) || 0;
  const abs = Math.abs(n);
  if (abs < 1_000) return n.toLocaleString();
  const sign = n < 0 ? "-" : "";
  const fmt = (v: number, suffix: string) => {
    const rounded = v >= 100 ? Math.round(v).toString() : v.toFixed(1).replace(/\.0$/, "");
    return `${sign}${rounded}${suffix}`;
  };
  if (abs < 1_000_000) return fmt(abs / 1_000, "K");
  if (abs < 1_000_000_000) return fmt(abs / 1_000_000, "M");
  return fmt(abs / 1_000_000_000, "B");
}

/**
 * Compact a currency amount for stat displays. e.g. 12,345 -> $12.3K
 * Uses formatCompactNumber under the hood.
 */
export function formatCompactCurrency(value: number | null | undefined, currency = "$"): string {
  const n = Number(value) || 0;
  const abs = Math.abs(n);
  if (abs < 1_000) {
    return `${currency}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  // >= $1,000: use K/M/B compact form (e.g. $2.6K, $1.2M).
  return `${currency}${formatCompactNumber(n)}`;
}

/**
 * Full (non-compacted) number formatting. Use for tooltips that reveal the precise value.
 */
export function formatFullNumber(value: number | null | undefined): string {
  const n = Number(value) || 0;
  return n.toLocaleString();
}

/**
 * Full (non-compacted) currency formatting. Use for tooltips that reveal the precise value.
 */
export function formatFullCurrency(value: number | null | undefined, currency = "$"): string {
  const n = Number(value) || 0;
  return `${currency}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Normalize a social media handle or URL into a full URL.
 * Supports Instagram, Facebook, TikTok handles (with or without @).
 */
export function normalizeSocialUrl(value: string, platform: 'instagram' | 'facebook' | 'tiktok' | 'website'): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  
  // Already a full URL
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  // Remove leading @ if present
  const handle = trimmed.startsWith('@') ? trimmed.slice(1) : trimmed;

  switch (platform) {
    case 'instagram':
      return `https://instagram.com/${handle}`;
    case 'facebook':
      return `https://facebook.com/${handle}`;
    case 'tiktok':
      return `https://tiktok.com/@${handle}`;
    case 'website':
      return `https://${trimmed}`;
    default:
      return `https://${trimmed}`;
  }
}
